/**
 * InsightPro™ — Management Report (print-only renderer)
 *
 * Phase 1: A4-paginated, print-styled HTML report. Sections:
 *   1. Cover               5. Cash Flow
 *   2. Executive Summary   6. Expense Analysis
 *   3. Profit & Loss       7. Entity Performance
 *   4. Balance Sheet       8. Geographic Split
 *                          9. CFO Commentary
 *                         10. Appendix
 *
 * Reads `?period=YYYY-MM&brand=BrandName` from URL. Reuses every existing
 * finance fetcher so it stays in sync as the data layer evolves.
 *
 * Sets `<body data-ready="true">` once all fetches resolve — puppeteer in
 * Phase 2 will use this as the wait-condition before calling page.pdf().
 *
 * Phase 1 = visual layer only. No PDF generation, no AI commentary. Open the
 * route in a browser and use Ctrl+P / Save as PDF for now.
 */
import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { signInWithCustomToken } from 'firebase/auth';
import {
  BarChart, Bar, XAxis, YAxis, Cell, LabelList,
  ResponsiveContainer, ReferenceLine,
} from 'recharts';
import { auth } from '../utils/firebase';
import { useCompany } from '../context/CompanyContext';
import {
  fetchGroupPL, fetchBalanceSheet, fetchCashFlow,
  fetchExpenseAnalysis, fetchEntityPLSummary, fetchFinancePeriods,
  fetchReportCommentary,
} from '../utils/api';

const GOLD     = '#c89520';
const GREEN    = '#1a7a3f';
const RED      = '#9b1c1c';
const NEUTRAL  = '#4a4a4a';
const LIGHTGREY = '#d8d8da';

const db = getFirestore();
const brandSlug = (b) => (b || 'all').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'all';
const noteId    = (period, brand) => `${period}__${brandSlug(brand)}`;

// ─── Formatters ─────────────────────────────────────────────────────────
const fmtMoney = (v) => {
  if (v == null || isNaN(v)) return '—';
  const sign = v < 0 ? '-' : '';
  const a = Math.abs(v);
  if (a >= 1_000_000) return sign + 'R' + (a / 1_000_000).toFixed(2) + 'M';
  if (a >= 1_000)     return sign + 'R' + (a / 1_000).toFixed(0)    + 'K';
  return sign + 'R' + a.toFixed(0);
};
const fmtFullMoney = (v) => {
  if (v == null || isNaN(v)) return '—';
  const sign = v < 0 ? '-' : '';
  return sign + 'R' + Math.abs(v).toLocaleString('en-ZA', { maximumFractionDigits: 0 });
};
const fmtPct = (v, d = 1) => (v == null || isNaN(v)) ? '—' : (v * 100).toFixed(d) + '%';
const fmtRatio = (v) => (v == null || isNaN(v)) ? '—' : v.toFixed(2);
const fmtSigned = (v) => {
  if (v == null || isNaN(v)) return '—';
  return (v > 0 ? '+' : '') + fmtMoney(v);
};
const todayISO = () => new Date().toISOString().split('T')[0];

// ─── Print/A4 styles ────────────────────────────────────────────────────
// All sizing is in mm so the on-screen preview matches the printed page.
const PRINT_CSS = `
  @page { size: A4 portrait; margin: 16mm 14mm 18mm 14mm; }
  @media print {
    aside, .layout-sidebar, .layout-header, .layout-footer, nav { display: none !important; }
    body, main, html { background: #ffffff !important; }
    .report-page { box-shadow: none; border: none; }
  }
  .report-root {
    font-family: var(--font-body, 'Inter', sans-serif);
    color: #0d0d0d;
    background: #f3f3f4;
    min-height: 100vh;
    padding: 24px 0;
    /* Disable fi/fl/ffi ligatures globally — the print font ships without
       them and Chromium drops the bare letters when the ligature glyph is
       missing (turning "profit" into "pro t"). Belt-and-braces with both
       properties so older browsers behave the same. */
    font-variant-ligatures: none;
    font-feature-settings: "liga" 0, "clig" 0, "dlig" 0;
  }
  .report-root *, .report-root *::before, .report-root *::after {
    font-variant-ligatures: none;
    font-feature-settings: "liga" 0, "clig" 0, "dlig" 0;
  }
  .report-page {
    width: 210mm;
    min-height: 297mm;
    background: #fff;
    margin: 0 auto 16px auto;
    padding: 16mm 14mm;
    box-shadow: 0 2px 18px rgba(0,0,0,0.08);
    page-break-after: always;
    box-sizing: border-box;
    position: relative;
  }
  .report-page:last-child { page-break-after: auto; }
  .report-h1 { font-family: var(--font-display, serif); font-size: 28pt; font-weight: 700; letter-spacing: -0.02em; margin: 0; color: #1a1a1a; }
  .report-h2 { font-family: var(--font-display, serif); font-size: 18pt; font-weight: 700; margin: 0 0 10mm 0; padding-bottom: 4mm; border-bottom: 2px solid #c89520; color: #1a1a1a; }
  .report-h3 { font-family: var(--font-mono, monospace); font-size: 9pt; letter-spacing: 0.14em; text-transform: uppercase; color: #6a6a6a; margin: 6mm 0 3mm 0; }
  .report-sub { font-family: var(--font-mono, monospace); font-size: 9pt; letter-spacing: 0.1em; text-transform: uppercase; color: #6a6a6a; }
  .report-kpi-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 4mm; margin-bottom: 8mm; }
  .report-kpi { border: 1px solid #d8d8da; border-radius: 2pt; padding: 4mm; }
  .report-kpi-lbl { font-family: var(--font-mono); font-size: 8pt; letter-spacing: 0.12em; text-transform: uppercase; color: #8a8a8a; margin-bottom: 2mm; }
  .report-kpi-val { font-family: var(--font-display); font-size: 16pt; font-weight: 700; color: #1a1a1a; line-height: 1.1; }
  .report-kpi-sub { font-family: var(--font-mono); font-size: 9pt; color: #6a6a6a; margin-top: 1.5mm; }
  .report-row    { display: flex; justify-content: space-between; align-items: baseline; padding: 2.5mm 0; border-bottom: 0.5pt solid #e5e5e5; font-size: 11pt; }
  .report-row-total { display: flex; justify-content: space-between; align-items: baseline; padding: 3mm 0; border-top: 1px solid #c89520; border-bottom: 1px solid #d8d8da; margin-top: 2mm; font-size: 12pt; font-weight: 700; color: #c89520; }
  .report-amt    { font-family: var(--font-mono); font-size: 11pt; }
  .report-amt-total { font-family: var(--font-mono); font-size: 12pt; font-weight: 700; color: #c89520; }
  .report-table  { width: 100%; border-collapse: collapse; font-size: 10pt; }
  .report-table th { font-family: var(--font-mono); font-size: 8pt; letter-spacing: 0.1em; text-transform: uppercase; color: #6a6a6a; text-align: left; padding: 3mm 2mm; border-bottom: 1px solid #c89520; }
  .report-table td { padding: 2.5mm 2mm; border-bottom: 0.5pt solid #ebebec; }
  .report-table .num { text-align: right; font-family: var(--font-mono); }
  .report-page-footer { position: absolute; bottom: 6mm; left: 14mm; right: 14mm; font-family: var(--font-mono); font-size: 8pt; color: #999; display: flex; justify-content: space-between; }
  .report-footer-note { font-family: var(--font-mono); font-size: 8pt; color: #6a6a6a; margin-top: 4mm; line-height: 1.5; }
  .report-empty  { font-family: var(--font-mono); font-size: 10pt; color: #999; font-style: italic; padding: 6mm 0; }
  .report-ai-stub { background: #faf6ec; border-left: 3px solid #c89520; padding: 4mm 5mm; margin: 4mm 0; font-size: 11pt; line-height: 1.55; color: #4a4a4a; font-style: italic; }
  .report-ai-stub::before { content: 'COMMENTARY '; display: inline-block; font-family: var(--font-mono); font-size: 8pt; letter-spacing: 0.14em; color: #c89520; font-style: normal; margin-right: 4mm; }
  .report-chart { width: 100%; margin: 4mm 0 6mm 0; }
  .report-chart text { font-family: var(--font-mono, monospace); font-size: 8pt; fill: #6a6a6a; }
  .report-ribbon { display: flex; height: 12mm; width: 100%; margin: 3mm 0 6mm 0; border-radius: 2pt; overflow: hidden; border: 1px solid #d8d8da; }
  .report-ribbon-seg { display: flex; flex-direction: column; align-items: center; justify-content: center; color: #fff; font-family: var(--font-mono); font-size: 9pt; padding: 0 2mm; min-width: 0; overflow: hidden; }
  .report-ribbon-seg-lbl { font-size: 7pt; letter-spacing: 0.1em; text-transform: uppercase; opacity: 0.85; }
  .report-ribbon-seg-val { font-weight: 700; font-size: 10pt; }
  .report-cover { display: flex; flex-direction: column; justify-content: space-between; min-height: 265mm; }
  .report-cover-mid { text-align: center; padding-top: 30mm; }
  .report-cover-logo { max-width: 80mm; max-height: 40mm; margin-bottom: 14mm; }
`;

// ─── Page header / footer ───────────────────────────────────────────────
function PageHeader({ title, period, brand, sectionLabel }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '8mm', paddingBottom: '3mm', borderBottom: '1px solid #d8d8da' }}>
      <div className="report-sub">{title} · {brand === 'Consolidated' ? 'All brands' : brand} · {period}</div>
      <div className="report-sub" style={{ color: '#c89520' }}>{sectionLabel}</div>
    </div>
  );
}
function PageFooter({ n, total }) {
  return (
    <div className="report-page-footer">
      <span>Generated by InsightPro™ · {todayISO()}</span>
      <span>Page {n} of {total}</span>
    </div>
  );
}

// ─── Component ─────────────────────────────────────────────────────────
export default function ReportRender() {
  const [params] = useSearchParams();
  const { selectedCompany } = useCompany();

  const period = params.get('period');
  const brand  = params.get('brand') || 'Consolidated';
  const shareToken = params.get('shareToken');

  // Share-token mode: puppeteer arrives with no selectedCompany. The token doc
  // carries companyId + logo + name, plus a customToken we sign in with. Once
  // authReady is true, the existing data fetchers work as usual.
  // Browser mode (no shareToken): selectedCompany comes from CompanyContext.
  const [shareMeta, setShareMeta] = useState(null);
  const [authReady, setAuthReady] = useState(!shareToken);
  const [authError, setAuthError] = useState(null);

  useEffect(() => {
    if (!shareToken) return;
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'reportTokens', shareToken));
        if (cancelled) return;
        if (!snap.exists()) { setAuthError('Share token not found.'); return; }
        const t = snap.data();
        const expMs = t.expiresAt?.toMillis ? t.expiresAt.toMillis() : new Date(t.expiresAt).getTime();
        if (expMs && expMs < Date.now()) { setAuthError('Share token expired.'); return; }
        setShareMeta({
          companyId:  t.companyId,
          companyName: t.companyName,
          tenantLogo: t.tenantLogo,
        });
        await signInWithCustomToken(auth, t.customToken);
        if (!cancelled) setAuthReady(true);
      } catch (e) {
        if (!cancelled) setAuthError(e.message || String(e));
      }
    })();
    return () => { cancelled = true; };
  }, [shareToken]);

  const companyId  = shareMeta?.companyId || selectedCompany?.id || selectedCompany?.companyId;
  const tenantLogo = shareMeta?.tenantLogo || selectedCompany?.logoUrl;
  const tenantName = shareMeta?.companyName || selectedCompany?.companyName || selectedCompany?.name || 'Tenant';

  const [brandDoc, setBrandDoc] = useState(null);   // { logoUrl, displayName, primaryColor }
  const [brandDocReady, setBrandDocReady] = useState(false);
  // Fetch the brand subcollection doc for this brand (one read on mount).
  // brandDocReady gates the data-ready flag below so puppeteer waits for the
  // logo to be in the DOM before snapshotting. Gated on authReady too: in
  // share-token mode the signInWithCustomToken must complete before Firestore
  // rules will permit the read (companyMember check).
  useEffect(() => {
    if (!authReady || !companyId) return;
    if (brand === 'Consolidated') { setBrandDocReady(true); return; }
    const bs = brand.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    getDoc(doc(db, 'companies', companyId, 'brands', bs))
      .then(snap => { if (snap.exists()) setBrandDoc(snap.data()); })
      .catch(() => {})
      .finally(() => setBrandDocReady(true));
  }, [authReady, companyId, brand]);

  // Per-brand logo:
  //   - Consolidated: tenant logoUrl (represents the whole group)
  //   - Per-brand:    ONLY the brand subcollection logoUrl
  // Falling back to tenant for a per-brand report risks showing the wrong
  // brand's logo (e.g. Pedros tenant logo on a Wackys report). Better to
  // degrade to a text-only cover and prompt the admin to upload a brand
  // logo via the Brands tab.
  const reportLogo  = brand === 'Consolidated' ? tenantLogo : (brandDoc?.logoUrl || null);
  const reportTitle = brand === 'Consolidated' ? tenantName : (brandDoc?.displayName || brand);

  // Tier-keyed data: per Pedros feedback (2026-05-12), the report splits into
  // Total (all entities) / Stores only / DCs only because DCs distort store-
  // level GP and ratio comparisons. Each tier holds its own pl/bs/cf/expense/
  // entities/byProvince. DC tier is skipped at render time when hasDCs=false.
  // Total deliberately includes non-operating overhead entities (HeadOffice,
  // Marketing, etc.) — Stores+DCs does NOT sum to Total; the gap is the
  // overhead. Methodology footnote in the Appendix explains this.
  const EMPTY_TIER = { pl: null, bs: null, cf: null, expense: null, entities: null, byProvince: null };
  const [tiers, setTiers] = useState({ total: EMPTY_TIER, stores: EMPTY_TIER, dcs: EMPTY_TIER });
  const [hasDCs, setHasDCs] = useState(false);
  const [note, setNote]           = useState(null);
  const [commentary, setCommentary] = useState(null);  // Phase 3 — { execSummary, pl, ... }
  const [commentaryReady, setCommentaryReady] = useState(false);
  const [periodLabel, setPeriodLabel] = useState(period);
  const [loading, setLoading]     = useState(true);
  const [err, setErr]             = useState(null);

  // Surface share-token auth failures to puppeteer via data-error attribute.
  // Without this the page would sit on the loading state until the function's
  // wait-for-data-ready hits its 60s timeout.
  useEffect(() => {
    if (authError) {
      document.body.setAttribute('data-error', authError);
      document.body.setAttribute('data-ready', 'error');
    }
  }, [authError]);

  // Fetch AI commentary (Phase 3). Non-blocking for page render — only the
  // data-ready signal below waits for it. Commentary errors degrade to the
  // placeholder text in render; they don't fail the report.
  useEffect(() => {
    if (loading || err || !authReady) return;
    let cancelled = false;
    const apiBrand = brand === 'Consolidated' ? 'all' : brand;
    fetchReportCommentary(period, apiBrand, { companyName: tenantName })
      .then(r => { if (!cancelled) { setCommentary(r.commentary); setCommentaryReady(true); } })
      .catch(() => { if (!cancelled) setCommentaryReady(true); });
    return () => { cancelled = true; };
  }, [loading, err, authReady, period, brand, tenantName]);

  // Puppeteer wait-condition. Four gates need to clear before we signal:
  //   1. main data fetch done (loading=false, no err)
  //   2. brand doc fetch done (so the logo URL is in state)
  //   3. commentary fetch done (Phase 3 — pulled or skipped on error)
  //   4. all <img> tags have finished loading (network fetch + paint)
  useEffect(() => {
    if (loading || err || !brandDocReady || !commentaryReady) return;
    let cancelled = false;
    requestAnimationFrame(async () => {
      const imgs = Array.from(document.querySelectorAll('img'));
      await Promise.all(imgs.map(img =>
        img.complete && img.naturalWidth > 0
          ? Promise.resolve()
          : new Promise(resolve => { img.onload = resolve; img.onerror = resolve; })
      ));
      if (cancelled) return;
      document.body.setAttribute('data-ready', 'true');
    });
    return () => { cancelled = true; };
  }, [loading, err, brandDocReady, commentaryReady]);

  // Three-tier fetch — Total / Stores / DCs. Each tier hits 6 endpoints, so
  // this is ~18 BigQuery queries per report render. fetchTier(null) = Total,
  // fetchTier('Store') = Stores, fetchTier('DC') = DCs. The data API filters
  // on DimEntity.entity_type at the SQL WHERE level — see functions/data-api/
  // index.js where every endpoint already accepts entityType.
  useEffect(() => {
    if (!period) { setErr('Missing ?period= query parameter.'); setLoading(false); return; }
    if (!authReady) return; // waits for signInWithCustomToken in share-token mode
    let cancelled = false;
    setLoading(true);
    const apiBrand = brand === 'Consolidated' ? 'all' : brand;
    const noteFetch = companyId
      ? getDoc(doc(db, 'companies', companyId, 'notes', noteId(period, brand)))
          .then(snap => snap.exists() ? snap.data() : null)
          .catch(() => null)
      : Promise.resolve(null);

    // Total and Stores respect the brand filter (apiBrand). DCs deliberately
    // do NOT — distribution centres typically serve the whole tenant, not a
    // single brand, so a per-brand report (?brand=Pedros) should still show
    // every DC. Without the override the DC tier would silently vanish for any
    // tenant whose DCs are tagged with brand=null or a corporate-level brand.
    // Methodology footnote in AppendixPage spells this out.
    const fetchTier = (entityType, brandOverride) => {
      const tierBrand = brandOverride ?? apiBrand;
      const f = entityType ? { entityType } : {};
      return Promise.all([
        fetchGroupPL(period, tierBrand, f),
        fetchBalanceSheet(period, tierBrand, f),
        fetchCashFlow(period, tierBrand, f),
        fetchExpenseAnalysis(period, tierBrand, { ...f, includeCoS: false }),
        fetchEntityPLSummary(period, tierBrand, { ...f, groupBy: 'entity', comparePeriod: 'priorMonth' }),
        fetchEntityPLSummary(period, tierBrand, { ...f, groupBy: 'province' }),
      ]).then(([pl, bs, cf, expense, entities, byProvince]) => ({
        pl, bs, cf, expense, entities, byProvince,
      }));
    };

    Promise.all([
      fetchTier(null),
      fetchTier('Store'),
      fetchTier('DC', 'all'), // DCs span brands — see comment on fetchTier
      fetchFinancePeriods(),
      noteFetch,
    ])
      .then(([total, stores, dcs, periods, n]) => {
        if (cancelled) return;
        setTiers({ total, stores, dcs });
        // hasDCs detection now uses the DC tier directly (since it ignores the
        // brand filter, we get the true tenant-wide DC count). Previously this
        // looked at total.entities and missed DCs whose brand didn't match the
        // current filter — the exact bug Imraan hit on 2026-05-12.
        const dcCount = (dcs.entities?.entities || []).length;
        setHasDCs(dcCount > 0);
        setNote(n);
        const pm = (periods.periods || []).find(x => x.periodKey === period);
        setPeriodLabel(pm?.monthName || period);
        setLoading(false);
        // data-ready signalling moved to a separate effect that also waits
        // for the brand doc fetch and any <img> tags (logo) to finish loading
        // — without that, puppeteer can snapshot before the logo paints.
      })
      .catch(e => {
        if (cancelled) return;
        const msg = e.response?.data?.error || e.message;
        setErr(msg);
        setLoading(false);
        document.body.setAttribute('data-error', msg || 'fetch failed');
        document.body.setAttribute('data-ready', 'error');
      });
    return () => { cancelled = true; };
  }, [period, brand, companyId, authReady]);

  if (loading) {
    return (
      <div style={{ padding: 40, fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--page-muted)' }}>
        Loading report data — period {period}, brand {brand}…
      </div>
    );
  }
  if (err) {
    return (
      <div style={{ padding: 40, fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--red)' }}>
        ⚠ {err}
      </div>
    );
  }

  // Tier list — DC tier is suppressed when zero DC entities exist for this
  // brand+period (e.g. single-brand retailer with no distribution). In that
  // case Total ≡ Stores, but we still render both for layout consistency.
  const tierList = [
    { key: 'total',  label: 'Total — All Entities',       short: 'TOTAL',  data: tiers.total },
    { key: 'stores', label: 'Stores Only',                short: 'STORES', data: tiers.stores },
    ...(hasDCs ? [{ key: 'dcs', label: 'Distribution Centres', short: 'DCs', data: tiers.dcs }] : []),
  ];

  // Page sequence. Each of the 6 financial sections renders once per tier;
  // cover / executive summary / CFO commentary / appendix are global.
  // Page numbers are computed from this list — no hard-coded n=2,3,4 anymore.
  const SECTION_KINDS = ['pl', 'bs', 'cf', 'exp', 'ent', 'prov'];
  const pages = [
    { kind: 'cover' },
    { kind: 'exec' },
    ...SECTION_KINDS.flatMap(kind => tierList.map(tier => ({ kind, tier }))),
    { kind: 'commentary' },
    { kind: 'appendix' },
  ];
  const totalPages = pages.length;

  const coverProps = { reportLogo, reportTitle, tenantName, brand, periodLabel };
  const headerProps = { reportTitle, periodLabel, brand };

  // AI commentary only attaches to the Total tier — Phase 3 fetches one
  // commentary object per (period, brand), not per (period, brand, tier).
  // Stores/DCs pages render the same section without the AI block, which
  // keeps the cost flat and avoids confusing duplicate prose.
  const commentaryForTier = (tier, key) =>
    tier.key === 'total' ? commentary?.[key] : null;

  return (
    <div className="report-root">
      <style>{PRINT_CSS}</style>

      {pages.map((p, i) => {
        const n = i + 1;
        switch (p.kind) {
          case 'cover':
            return <CoverPage key={n} {...coverProps} />;
          case 'exec':
            return <ExecPage  key={n} {...headerProps} tierList={tierList} commentary={commentary} pageNum={n} totalPages={totalPages} />;
          case 'pl':
            return <PLPage    key={n} {...headerProps} tier={p.tier} commentary={commentaryForTier(p.tier, 'pl')}           pageNum={n} totalPages={totalPages} />;
          case 'bs':
            return <BSPage    key={n} {...headerProps} tier={p.tier} commentary={commentaryForTier(p.tier, 'balanceSheet')} pageNum={n} totalPages={totalPages} />;
          case 'cf':
            return <CFPage    key={n} {...headerProps} tier={p.tier} commentary={commentaryForTier(p.tier, 'cashFlow')}     pageNum={n} totalPages={totalPages} />;
          case 'exp':
            return <ExpPage   key={n} {...headerProps} tier={p.tier} commentary={commentaryForTier(p.tier, 'expense')}      pageNum={n} totalPages={totalPages} />;
          case 'ent':
            return <EntPage   key={n} {...headerProps} tier={p.tier} commentary={commentaryForTier(p.tier, 'entities')}     pageNum={n} totalPages={totalPages} />;
          case 'prov':
            return <ProvPage  key={n} {...headerProps} tier={p.tier} commentary={commentaryForTier(p.tier, 'geography')}    pageNum={n} totalPages={totalPages} />;
          case 'commentary':
            return <CommentaryPage key={n} {...headerProps} note={note} pageNum={n} totalPages={totalPages} />;
          case 'appendix':
            return <AppendixPage   key={n} {...headerProps} hasDCs={hasDCs} pageNum={n} totalPages={totalPages} />;
          default:
            return null;
        }
      })}
    </div>
  );
}

// ─── Small render helpers ──────────────────────────────────────────────
// AI commentary block. Real prose from Claude when present; visually distinct
// placeholder when missing (so the report still hangs together and the PDF
// reader can see where the AI section will land).
function AIComment({ text, placeholder }) {
  if (text) {
    return <div className="report-ai-stub">{text}</div>;
  }
  return (
    <div className="report-ai-stub" style={{ opacity: 0.6, fontStyle: 'italic' }}>
      {placeholder}
    </div>
  );
}

function Kpi({ label, value, sub }) {
  return (
    <div className="report-kpi">
      <div className="report-kpi-lbl">{label}</div>
      <div className="report-kpi-val">{value}</div>
      {sub && <div className="report-kpi-sub">{sub}</div>}
    </div>
  );
}
function Row({ label, amount, bold, sub, ratio }) {
  const fmt = ratio ? fmtRatio : fmtFullMoney;
  return (
    <div className="report-row">
      <div style={{ fontWeight: bold ? 700 : 400 }}>
        {label}
        {sub && <span style={{ marginLeft: '4mm', fontFamily: 'var(--font-mono)', fontSize: '8pt', color: '#999' }}>{sub}</span>}
      </div>
      <div className="report-amt" style={{ fontWeight: bold ? 700 : 400 }}>{fmt(amount)}</div>
    </div>
  );
}
function RowTotal({ label, amount, sub }) {
  return (
    <div className="report-row-total">
      <div>
        {label}
        {sub && <span style={{ marginLeft: '4mm', fontFamily: 'var(--font-mono)', fontSize: '8pt', color: '#c89520' }}>{sub}</span>}
      </div>
      <div className="report-amt-total">{fmtFullMoney(amount)}</div>
    </div>
  );
}
function EntityTable({ rows }) {
  if (!rows?.length) return <div className="report-empty">No entities to show.</div>;
  return (
    <table className="report-table">
      <thead>
        <tr>
          <th>Entity</th>
          <th>Brand</th>
          <th>Type</th>
          <th className="num">Sales</th>
          <th className="num">EBITDA</th>
          <th className="num">Margin</th>
          <th className="num">vs PM</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(e => (
          <tr key={e.entityKey}>
            <td>{e.legalName}</td>
            <td>{e.brand || '—'}</td>
            <td>{e.entityType || '—'}</td>
            <td className="num">{fmtFullMoney(e.sales)}</td>
            <td className="num">{fmtFullMoney(e.ebitda)}</td>
            <td className="num">{fmtPct(e.ebitdaPct)}</td>
            <td className="num" style={{ color: (e.ebitdaDelta ?? 0) >= 0 ? '#1a7a3f' : '#9b1c1c' }}>
              {fmtSigned(e.ebitdaDelta)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ─── Chart helpers ──────────────────────────────────────────────────────
// All charts use isAnimationActive={false} so puppeteer's data-ready signal
// catches the final state synchronously after data lands. ResponsiveContainer
// reads its parent's mm-sized width; fix the parent height in mm so the
// SVG draws at print resolution.

const tickStyle = { fontSize: 8, fill: '#6a6a6a', fontFamily: 'monospace' };

function RevenueRibbon({ pl }) {
  if (!pl?.sales || pl.sales <= 0) return null;
  const sales = pl.sales || 0;
  const cos   = Math.abs(pl.costOfSales || 0);
  const opex  = Math.abs(pl.otherExpense || 0);
  const ebitda = Math.max(0, sales - cos - opex);
  const total = cos + opex + ebitda || 1;
  const segs = [
    { label: 'Cost of Sales',  value: cos,    color: '#7c1d1d' },
    { label: 'Operating Exp.', value: opex,   color: '#b8842b' },
    { label: 'EBITDA',         value: ebitda, color: '#1a7a3f' },
  ];
  return (
    <div className="report-ribbon">
      {segs.map(s => {
        const pct = (s.value / total) * 100;
        if (pct < 0.5) return null;
        return (
          <div key={s.label} className="report-ribbon-seg" style={{ width: pct + '%', background: s.color }}>
            <div className="report-ribbon-seg-lbl">{s.label}</div>
            <div className="report-ribbon-seg-val">{pct.toFixed(1)}%</div>
          </div>
        );
      })}
    </div>
  );
}

function PLBarChart({ pl }) {
  if (!pl) return null;
  const data = [
    { name: 'Revenue',  value: pl.revenue || 0,                   fill: NEUTRAL },
    { name: 'GP',       value: pl.grossProfit || 0,               fill: GOLD },
    { name: 'OpEx',     value: Math.abs(pl.otherExpense || 0),    fill: RED },
    { name: 'EBITDA',   value: pl.ebitda || 0,                    fill: (pl.ebitda || 0) >= 0 ? GREEN : RED },
  ];
  return (
    <div className="report-chart" style={{ height: '55mm' }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 14, right: 12, left: 12, bottom: 4 }}>
          <XAxis dataKey="name" tick={tickStyle} axisLine={{ stroke: LIGHTGREY }} tickLine={false} />
          <YAxis hide />
          <Bar dataKey="value" isAnimationActive={false} radius={[2, 2, 0, 0]}>
            {data.map((d, i) => <Cell key={i} fill={d.fill} />)}
            <LabelList dataKey="value" position="top" formatter={fmtMoney}
                       style={{ fontFamily: 'monospace', fontSize: 9, fill: '#333' }} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function BSStackChart({ totals }) {
  if (!totals) return null;
  const assets = totals.assets || 0;
  const liab   = totals.liabilities || 0;
  const equity = totals.equity || 0;
  const liabEq = liab + equity || 1;
  // Two horizontal stacked rows: top = Total Assets, bottom = Liab + Equity.
  // Visual sanity check on accounting equation.
  return (
    <div className="report-chart" style={{ height: '32mm', display: 'flex', flexDirection: 'column', gap: '3mm' }}>
      <StackRow label="Assets" segs={[
        { label: 'Total Assets', value: assets, color: GOLD },
      ]} total={Math.max(assets, liabEq)} />
      <StackRow label="Liab + Equity" segs={[
        { label: 'Liabilities', value: liab,   color: RED },
        { label: 'Equity',      value: equity, color: GREEN },
      ]} total={Math.max(assets, liabEq)} />
    </div>
  );
}

function StackRow({ label, segs, total }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '3mm' }}>
      <div style={{ width: '24mm', fontFamily: 'monospace', fontSize: 9, color: '#6a6a6a', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{label}</div>
      <div style={{ display: 'flex', flex: 1, height: '10mm', borderRadius: '2pt', overflow: 'hidden', border: '1px solid #d8d8da' }}>
        {segs.map(s => {
          const w = (s.value / total) * 100;
          if (w < 0.5) return null;
          return (
            <div key={s.label} title={s.label}
                 style={{
                   width: w + '%', background: s.color, color: '#fff',
                   display: 'flex', alignItems: 'center', justifyContent: 'center',
                   fontFamily: 'monospace', fontSize: 8.5, letterSpacing: '0.05em',
                 }}>
              {s.label} {fmtMoney(s.value)}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function CFBridgeChart({ cf }) {
  if (!cf?.operating) return null;
  const ni  = cf.operating.netIncome || 0;
  const da  = cf.operating.depreciationAmortisation || 0;
  const wc  = cf.operating.workingCapitalChange || 0;
  const ocf = cf.operating.operatingCashFlow || 0;
  const data = [
    { name: 'Net Income', value: ni,   fill: ni  >= 0 ? GREEN : RED },
    { name: '+ D&A',      value: da,   fill: GOLD },
    { name: '± ΔWC',      value: wc,   fill: wc  >= 0 ? GREEN : RED },
    { name: '= OCF',      value: ocf,  fill: ocf >= 0 ? '#0d4d29' : '#5b1010' },
  ];
  return (
    <div className="report-chart" style={{ height: '55mm' }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 14, right: 12, left: 12, bottom: 4 }}>
          <ReferenceLine y={0} stroke="#8a8a8a" />
          <XAxis dataKey="name" tick={tickStyle} axisLine={{ stroke: LIGHTGREY }} tickLine={false} />
          <YAxis hide />
          <Bar dataKey="value" isAnimationActive={false} radius={[2, 2, 2, 2]}>
            {data.map((d, i) => <Cell key={i} fill={d.fill} />)}
            <LabelList dataKey="value" position="top" formatter={fmtMoney}
                       style={{ fontFamily: 'monospace', fontSize: 9, fill: '#333' }} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function ExpenseBarChart({ categories }) {
  if (!categories?.length) return null;
  // Horizontal bar — top categories by amount.
  const data = categories.slice(0, 10).map(c => ({
    name: c.reportCategory || '—',
    value: Math.abs(c.amount || 0),
    pct: c.pctOfSales,
  }));
  const heightMm = 6 + data.length * 6; // grows with category count
  return (
    <div className="report-chart" style={{ height: heightMm + 'mm' }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} layout="vertical" margin={{ top: 4, right: 60, left: 10, bottom: 4 }}>
          <XAxis type="number" hide />
          <YAxis type="category" dataKey="name" width={120} tick={tickStyle}
                 axisLine={false} tickLine={false} />
          <Bar dataKey="value" fill={GOLD} isAnimationActive={false} radius={[0, 2, 2, 0]}>
            <LabelList dataKey="value" position="right" formatter={fmtMoney}
                       style={{ fontFamily: 'monospace', fontSize: 9, fill: '#333' }} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function EntityBarChart({ rows }) {
  if (!rows?.length) return null;
  const data = rows.slice(0, 8).map(e => ({
    name: e.legalName || e.entityKey,
    value: e.ebitda || 0,
    pct: e.ebitdaPct,
  }));
  const heightMm = 6 + data.length * 6;
  return (
    <div className="report-chart" style={{ height: heightMm + 'mm' }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} layout="vertical" margin={{ top: 4, right: 70, left: 10, bottom: 4 }}>
          <ReferenceLine x={0} stroke="#8a8a8a" />
          <XAxis type="number" hide />
          <YAxis type="category" dataKey="name" width={140} tick={tickStyle}
                 axisLine={false} tickLine={false} />
          <Bar dataKey="value" isAnimationActive={false} radius={[0, 2, 2, 0]}>
            {data.map((d, i) => <Cell key={i} fill={d.value >= 0 ? GREEN : RED} />)}
            <LabelList dataKey="value" position="right" formatter={fmtMoney}
                       style={{ fontFamily: 'monospace', fontSize: 9, fill: '#333' }} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function ProvinceBarChart({ rows }) {
  if (!rows?.length) return null;
  const data = rows
    .filter(r => r.sales != null)
    .sort((a, b) => (b.sales || 0) - (a.sales || 0))
    .slice(0, 12)
    .map(r => ({
      name: r.province || '— Unassigned —',
      value: r.sales || 0,
    }));
  if (!data.length) return null;
  const heightMm = 6 + data.length * 6;
  return (
    <div className="report-chart" style={{ height: heightMm + 'mm' }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} layout="vertical" margin={{ top: 4, right: 60, left: 10, bottom: 4 }}>
          <XAxis type="number" hide />
          <YAxis type="category" dataKey="name" width={130} tick={tickStyle}
                 axisLine={false} tickLine={false} />
          <Bar dataKey="value" fill={GOLD} isAnimationActive={false} radius={[0, 2, 2, 0]}>
            <LabelList dataKey="value" position="right" formatter={fmtMoney}
                       style={{ fontFamily: 'monospace', fontSize: 9, fill: '#333' }} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

// ─── Page components ────────────────────────────────────────────────────
// Each financial section page receives `tier` ({key,label,short,data}) plus
// the standard header context. tier.data carries the section's data
// (pl/bs/cf/expense/entities/byProvince) — already filtered server-side by
// entityType. Total tier uses entityType=null so it includes all entity
// types including non-operating overhead (HeadOffice, Marketing, etc.);
// the methodology footnote in AppendixPage explains why Stores+DCs ≠ Total.

// Section banner shown on every tiered page — replaces the previous flat
// "Profit & Loss" h2. Includes a coloured pill for the tier so the reader
// always knows which slice they're looking at.
function TierBanner({ title, tier }) {
  // Colour-code the tier pill so DC vs Store pages are visually distinguishable.
  const tierColor = tier.key === 'total' ? '#4a4a4a'
                  : tier.key === 'stores' ? '#1a7a3f'
                  : '#7c1d1d'; // DCs
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: '8mm', paddingBottom: '4mm', borderBottom: '2px solid #c89520' }}>
      <h2 className="report-h2" style={{ border: 'none', margin: 0, padding: 0 }}>{title}</h2>
      <span style={{
        fontFamily: 'var(--font-mono)', fontSize: '9pt', letterSpacing: '0.14em',
        textTransform: 'uppercase', color: '#fff', background: tierColor,
        padding: '1.5mm 3mm', borderRadius: '2pt',
      }}>{tier.label}</span>
    </div>
  );
}

function CoverPage({ reportLogo, reportTitle, tenantName, brand, periodLabel }) {
  return (
    <section className="report-page">
      <div className="report-cover">
        <div>
          <div className="report-sub" style={{ marginBottom: '4mm' }}>InsightPro™ · Management Report</div>
          <div style={{ height: '1px', background: '#c89520', width: '40mm' }} />
        </div>
        <div className="report-cover-mid">
          {reportLogo && <img src={reportLogo} alt={reportTitle} className="report-cover-logo" />}
          <div className="report-h1" style={{ fontSize: '34pt', marginBottom: '6mm' }}>{reportTitle}</div>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: '20pt', fontWeight: 400, color: '#4a4a4a', marginBottom: '3mm' }}>
            Management Report
          </div>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: '12pt', letterSpacing: '0.1em', color: '#6a6a6a' }}>
            {periodLabel} · {brand === 'Consolidated' ? `${tenantName} — All Brands` : `${tenantName} — ${brand} brand`}
          </div>
        </div>
        <div>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: '9pt', letterSpacing: '0.1em', color: '#8a8a8a', textTransform: 'uppercase' }}>
            Generated {todayISO()}
          </div>
          <div className="report-footer-note">
            Prepared by InsightPro™. This report reflects the data state at time of generation. Any subsequent corrections to the underlying figures will be reflected in future reports — see the Appendix for data lineage notes.
          </div>
        </div>
      </div>
    </section>
  );
}

// Exec Summary is the only page that shows ALL tiers side-by-side. One column
// per tier, three KPI rows each. RevenueRibbon stays single (Total) — it's a
// unit-economics view, not a tier comparison.
function ExecPage({ reportTitle, periodLabel, brand, tierList, commentary, pageNum, totalPages }) {
  const cols = tierList.length;
  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: `repeat(${cols}, 1fr)`,
    gap: '4mm',
    marginBottom: '8mm',
  };
  return (
    <section className="report-page">
      <PageHeader title={reportTitle} period={periodLabel} brand={brand} sectionLabel="EXECUTIVE SUMMARY" />
      <div className="report-h2">Executive Summary</div>

      <div style={gridStyle}>
        {tierList.map(t => {
          const pl = t.data.pl;
          const bs = t.data.bs;
          const cf = t.data.cf;
          const tierColor = t.key === 'total' ? '#4a4a4a' : t.key === 'stores' ? '#1a7a3f' : '#7c1d1d';
          return (
            <div key={t.key} style={{ border: '1px solid #d8d8da', borderTop: '3px solid ' + tierColor, padding: '4mm', borderRadius: '2pt' }}>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: '8pt', letterSpacing: '0.14em', textTransform: 'uppercase', color: tierColor, marginBottom: '3mm', fontWeight: 700 }}>
                {t.label}
              </div>
              <ExecKpi label="Revenue"        value={fmtMoney(pl?.revenue)} />
              <ExecKpi label="Gross Profit"   value={fmtMoney(pl?.grossProfit)}  sub={fmtPct(pl?.grossProfitPct)} />
              <ExecKpi label="EBITDA"         value={fmtMoney(pl?.ebitda)}       sub={fmtPct(pl?.ebitdaPct)} />
              <ExecKpi label="Cash on Hand"   value={fmtMoney(cf?.cashBalance)} />
              <ExecKpi label="Working Cap."   value={fmtMoney(bs?.totals?.workingCapital)} />
              <ExecKpi label="Current Ratio"  value={fmtRatio(bs?.totals?.currentRatio)} />
            </div>
          );
        })}
      </div>

      <div className="report-h3">How every R1 of revenue is spent (total)</div>
      <RevenueRibbon pl={tierList[0]?.data?.pl} />

      <AIComment text={commentary?.execSummary} placeholder="Executive commentary will appear here — a headline narrative covering the period's main story (top variances, drivers, outlook caveats), grounded in the figures above." />

      <div className="report-h3">What's in this report</div>
      <ol style={{ fontSize: '9.5pt', lineHeight: 1.6, color: '#4a4a4a', margin: 0, paddingLeft: '8mm' }}>
        <li>Profit &amp; Loss — line-by-line, per tier (Total / Stores{tierList.length > 2 ? ' / DCs' : ''})</li>
        <li>Balance Sheet — assets, liabilities, equity, key ratios per tier</li>
        <li>Cash Flow — operating, investing, financing flows per tier</li>
        <li>Expense Analysis — composition of operating expense by category, per tier</li>
        <li>Entity Performance — top and bottom performers by EBITDA, per tier</li>
        <li>Geographic Split — performance by province, per tier</li>
        <li>CFO Commentary — narrative notes from the finance team</li>
        <li>Appendix — methodology, data lineage, footnotes</li>
      </ol>

      <PageFooter n={pageNum} total={totalPages} />
    </section>
  );
}

function ExecKpi({ label, value, sub }) {
  return (
    <div style={{ padding: '1.5mm 0', borderBottom: '0.5pt solid #ebebec', display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
      <span style={{ fontFamily: 'var(--font-mono)', fontSize: '8pt', color: '#6a6a6a', letterSpacing: '0.05em' }}>{label}</span>
      <span style={{ fontFamily: 'var(--font-display)', fontSize: '12pt', fontWeight: 700, color: '#1a1a1a' }}>
        {value}
        {sub && <span style={{ marginLeft: '2mm', fontFamily: 'var(--font-mono)', fontSize: '8pt', color: '#999', fontWeight: 400 }}>{sub}</span>}
      </span>
    </div>
  );
}

function PLPage({ reportTitle, periodLabel, brand, tier, commentary, pageNum, totalPages }) {
  const pl = tier.data.pl;
  return (
    <section className="report-page">
      <PageHeader title={reportTitle} period={periodLabel} brand={brand} sectionLabel={`PROFIT & LOSS · ${tier.short}`} />
      <TierBanner title="Profit & Loss" tier={tier} />
      {pl ? (
        <>
          <PLBarChart pl={pl} />
          <Row label="Sales"                amount={pl.sales} />
          <Row label="+ Other Income"       amount={pl.otherIncome} />
          <Row label="= Revenue"            amount={pl.revenue} bold />
          <Row label="− Cost of Sales"      amount={pl.costOfSales} />
          <Row label="= Gross Profit"       amount={pl.grossProfit} bold sub={fmtPct(pl.grossProfitPct) + ' margin'} />
          <Row label="− Operating Expenses" amount={pl.otherExpense} />
          <RowTotal label="EBITDA"          amount={pl.ebitda} sub={fmtPct(pl.ebitdaPct) + ' margin'} />
        </>
      ) : <div className="report-empty">No P&amp;L data for this tier.</div>}
      {commentary !== null && (
        <AIComment text={commentary} placeholder="AI variance commentary will appear here — material movements vs prior period and likely drivers." />
      )}
      <PageFooter n={pageNum} total={totalPages} />
    </section>
  );
}

function BSPage({ reportTitle, periodLabel, brand, tier, commentary, pageNum, totalPages }) {
  const bs = tier.data.bs;
  // Group bs.sections by accountType — server-side ordering is by ABS(total) DESC
  // within each type, so biggest categories appear first. Per Rayhaan #6: show
  // the line-item breakdown, not just the rolled-up Assets / Liabilities totals.
  const sectionsBy = (type) => (bs?.sections || []).filter(s => s.accountType === type);
  const assets = sectionsBy('Asset');
  const liabs  = sectionsBy('Liability');
  const equity = sectionsBy('Equity');
  return (
    <section className="report-page">
      <PageHeader title={reportTitle} period={periodLabel} brand={brand} sectionLabel={`BALANCE SHEET · ${tier.short}`} />
      <TierBanner title="Balance Sheet" tier={tier} />
      {bs?.totals ? (
        <>
          <BSStackChart totals={bs.totals} />

          <div className="report-h3">Assets</div>
          {assets.length > 0
            ? assets.map(s => <Row key={'a-' + s.reportCategory} label={s.reportCategory || '(uncategorised)'} amount={s.total} />)
            : <Row label="(no asset sections reported)" amount={null} />}
          <RowTotal label="Total Assets" amount={bs.totals.assets} />

          <div className="report-h3" style={{ marginTop: '5mm' }}>Liabilities</div>
          {liabs.length > 0
            ? liabs.map(s => <Row key={'l-' + s.reportCategory} label={s.reportCategory || '(uncategorised)'} amount={s.total} />)
            : <Row label="(no liability sections reported)" amount={null} />}
          <RowTotal label="Total Liabilities" amount={bs.totals.liabilities} />

          <div className="report-h3" style={{ marginTop: '5mm' }}>Equity</div>
          {equity.length > 0
            ? equity.map(s => <Row key={'e-' + s.reportCategory} label={s.reportCategory || '(uncategorised)'} amount={s.total} />)
            : <Row label="(no equity sections reported)" amount={null} />}
          <RowTotal label="Total Equity" amount={bs.totals.equity} />

          <div className="report-h3" style={{ marginTop: '5mm' }}>Key ratios</div>
          <Row label="Working Capital (Cur. Assets − Cur. Liab.)" amount={bs.totals.workingCapital} />
          <Row label="Current Ratio (Cur. Assets ÷ Cur. Liab.)"   amount={bs.totals.currentRatio} ratio />
          <Row label="Debt-to-Equity (Liab. ÷ Equity)"            amount={bs.totals.debtEquity} ratio />
        </>
      ) : <div className="report-empty">No Balance Sheet data for this tier.</div>}
      {commentary !== null && (
        <AIComment text={commentary} placeholder="AI liquidity + leverage assessment — working capital, current ratio, debt-to-equity, phrased as a CFO would." />
      )}
      <PageFooter n={pageNum} total={totalPages} />
    </section>
  );
}

function CFPage({ reportTitle, periodLabel, brand, tier, commentary, pageNum, totalPages }) {
  const cf = tier.data.cf;
  return (
    <section className="report-page">
      <PageHeader title={reportTitle} period={periodLabel} brand={brand} sectionLabel={`CASH FLOW · ${tier.short}`} />
      <TierBanner title="Cash Flow" tier={tier} />
      {cf ? (
        <>
          <CFBridgeChart cf={cf} />
          <div className="report-h3">Operating activities</div>
          <Row label="Net Income (EBITDA proxy)"          amount={cf.operating?.netIncome} />
          <Row label="+ Depreciation &amp; Amortisation"  amount={cf.operating?.depreciationAmortisation} />
          <Row label="± Δ Working Capital"                amount={cf.operating?.workingCapitalChange}
               sub={cf.operating?.workingCapitalChange == null ? '(pending multi-period BS)' : null} />
          <RowTotal label="Operating Cash Flow"           amount={cf.operating?.operatingCashFlow} />

          <div className="report-h3">Investing &amp; Financing</div>
          <Row label="Investing Cash Flow" amount={cf.investing?.investingCashFlow} sub="(when historical BS available)" />
          <Row label="Financing Cash Flow" amount={cf.financing?.financingCashFlow} sub="(when historical BS available)" />
          <RowTotal label="Net Change in Cash" amount={cf.netChangeInCash} sub={cf.hasPriorBS ? null : 'needs prior period BS'} />

          <div className="report-h3">Position</div>
          <Row label="Cash on Hand (period end)" amount={cf.cashBalance} bold />
        </>
      ) : <div className="report-empty">No Cash Flow data for this tier.</div>}
      {commentary !== null && (
        <AIComment text={commentary} placeholder="AI quality-of-earnings comment — how much of EBITDA is converting to cash, working-capital drag, runway concerns." />
      )}
      <PageFooter n={pageNum} total={totalPages} />
    </section>
  );
}

function ExpPage({ reportTitle, periodLabel, brand, tier, commentary, pageNum, totalPages }) {
  const expense = tier.data.expense;
  const topCategories = (expense?.categories || []).slice(0, 10);
  return (
    <section className="report-page">
      <PageHeader title={reportTitle} period={periodLabel} brand={brand} sectionLabel={`EXPENSE ANALYSIS · ${tier.short}`} />
      <TierBanner title="Operating Expense Composition" tier={tier} />
      {topCategories.length ? (
        <>
          <ExpenseBarChart categories={topCategories} />
          <table className="report-table">
            <thead>
              <tr>
                <th>Category</th>
                <th className="num">Amount</th>
                <th className="num">% of Sales</th>
                <th className="num">% of OpEx</th>
              </tr>
            </thead>
            <tbody>
              {(() => {
                const total = topCategories.reduce((s, c) => s + (c.amount || 0), 0) || 1;
                return topCategories.map(c => (
                  <tr key={c.reportCategory}>
                    <td>{c.reportCategory}</td>
                    <td className="num">{fmtFullMoney(c.amount)}</td>
                    <td className="num">{fmtPct(c.pctOfSales)}</td>
                    <td className="num">{fmtPct((c.amount || 0) / total)}</td>
                  </tr>
                ));
              })()}
            </tbody>
          </table>
        </>
      ) : <div className="report-empty">No expense data for this tier.</div>}
      {commentary !== null && (
        <AIComment text={commentary} placeholder="AI cost-management commentary — categories trending up MoM, opportunities for action, top levers for the next period." />
      )}
      <PageFooter n={pageNum} total={totalPages} />
    </section>
  );
}

function EntPage({ reportTitle, periodLabel, brand, tier, commentary, pageNum, totalPages }) {
  // Entity Performance pre-filters EBITDA-null rows then sorts. Top-N count
  // is smaller for non-Total tiers because there are fewer entities to rank.
  const all = (tier.data.entities?.entities || []).filter(e => e.ebitda != null);
  const topN    = tier.key === 'total' ? 8 : 6;
  const bottomN = tier.key === 'total' ? 5 : 4;
  const top     = [...all].sort((a, b) => (b.ebitda || 0) - (a.ebitda || 0)).slice(0, topN);
  const bottom  = [...all].sort((a, b) => (a.ebitda || 0) - (b.ebitda || 0)).slice(0, bottomN);
  return (
    <section className="report-page">
      <PageHeader title={reportTitle} period={periodLabel} brand={brand} sectionLabel={`ENTITY PERFORMANCE · ${tier.short}`} />
      <TierBanner title="Entity Performance" tier={tier} />
      {all.length ? (
        <>
          <EntityBarChart rows={top} />
          <div className="report-h3">Top {top.length} by EBITDA</div>
          <EntityTable rows={top} />
          {bottom.length > 0 && top[0]?.entityKey !== bottom[0]?.entityKey && (
            <>
              <div className="report-h3" style={{ marginTop: '6mm' }}>Bottom {bottom.length} by EBITDA</div>
              <EntityTable rows={bottom} />
            </>
          )}
        </>
      ) : <div className="report-empty">No entities in this tier.</div>}
      {commentary !== null && (
        <AIComment text={commentary} placeholder="AI store-level synthesis — outperformers + hypothesised drivers, underperformers + recommended interventions." />
      )}
      <PageFooter n={pageNum} total={totalPages} />
    </section>
  );
}

function ProvPage({ reportTitle, periodLabel, brand, tier, commentary, pageNum, totalPages }) {
  const rows = tier.data.byProvince?.entities || [];
  return (
    <section className="report-page">
      <PageHeader title={reportTitle} period={periodLabel} brand={brand} sectionLabel={`GEOGRAPHIC SPLIT · ${tier.short}`} />
      <TierBanner title="Performance by Province" tier={tier} />
      {rows.length ? (
        <>
          <ProvinceBarChart rows={rows} />
          <table className="report-table">
            <thead>
              <tr>
                <th>Province / Region</th>
                <th>Country</th>
                <th className="num">Sales</th>
                <th className="num">EBITDA</th>
                <th className="num">Margin</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={(r.province || 'unknown') + '_' + (r.country || '') + '_' + i}>
                  <td>{r.province || '— Unassigned —'}</td>
                  <td>{r.country || '—'}</td>
                  <td className="num">{fmtFullMoney(r.sales)}</td>
                  <td className="num">{fmtFullMoney(r.ebitda)}</td>
                  <td className="num">{fmtPct(r.ebitdaPct)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      ) : <div className="report-empty">No province breakdown for this tier — entities may not have province set.</div>}
      {commentary !== null && (
        <AIComment text={commentary} placeholder="Geographic synthesis — concentration risk, growth pockets, regions trending vs national average." />
      )}
      <PageFooter n={pageNum} total={totalPages} />
    </section>
  );
}

function CommentaryPage({ reportTitle, periodLabel, brand, note, pageNum, totalPages }) {
  return (
    <section className="report-page">
      <PageHeader title={reportTitle} period={periodLabel} brand={brand} sectionLabel="CFO COMMENTARY" />
      <div className="report-h2">CFO Commentary</div>
      {note?.body ? (
        <div style={{ fontSize: '11pt', lineHeight: 1.7, color: '#2a2a2a', whiteSpace: 'pre-wrap' }}>{note.body}</div>
      ) : (
        <div className="report-empty">No CFO commentary has been entered for {periodLabel} ({brand}). Add via Notes &amp; Commentary in the portal.</div>
      )}
      <PageFooter n={pageNum} total={totalPages} />
    </section>
  );
}

function AppendixPage({ reportTitle, periodLabel, brand, hasDCs, pageNum, totalPages }) {
  return (
    <section className="report-page">
      <PageHeader title={reportTitle} period={periodLabel} brand={brand} sectionLabel="APPENDIX" />
      <div className="report-h2">Appendix</div>

      <div className="report-h3">Methodology</div>
      <ul style={{ fontSize: '10pt', lineHeight: 1.7, color: '#4a4a4a', marginTop: 0, paddingLeft: '6mm' }}>
        <li>Each financial section appears once per tier: <strong>Total</strong> (all entities), <strong>Stores</strong> (entity_type=Store){hasDCs && <>, <strong>DCs</strong> (entity_type=DC)</>}. Tiers are filtered server-side via <code>DimEntity.entity_type</code>.</li>
        <li><strong>Total ≠ Stores + DCs</strong>: Total also includes non-operating entities (HeadOffice, Marketing, Recruitment, Property) that aren't broken out separately. The difference between Total and the sum of operating tiers represents central / overhead activity.</li>
        {hasDCs && (
          <li><strong>DC tier scope:</strong> on a per-brand report, the Total and Stores tiers are filtered to the selected brand. The DCs tier is <strong>not</strong> brand-filtered — distribution centres typically serve the whole tenant, so every DC appears regardless of which brand the report is scoped to. Consolidated reports show all DCs either way.</li>
        )}
        <li>EBITDA is an estimate — depreciation &amp; amortisation are inferred from the GL where not separately reported.</li>
        <li>Cash Flow operating section uses the indirect method. WC/Investing/Financing sections require prior-period Balance Sheet data; rows show "—" when not yet available.</li>
        <li>Group consolidation: each entity contributes at its functional currency; FX translation uses period-end rates.</li>
        <li>Entity performance comparisons are vs the prior month unless stated otherwise.</li>
      </ul>

      <div className="report-h3">Data lineage</div>
      <div style={{ fontSize: '10pt', lineHeight: 1.7, color: '#4a4a4a' }}>
        Source: Sage Evolution monthly pack uploaded via the InsightPro™ portal. All figures derived from <code>FactGLMonthly</code> and <code>FactBSMonthly</code> in the tenant's BigQuery dataset, joined to <code>DimEntity</code> and <code>DimGLAccount</code>.
      </div>

      <div className="report-h3">Footnotes</div>
      <div style={{ fontSize: '10pt', lineHeight: 1.7, color: '#4a4a4a' }}>
        [Phase 3] Per-report data-quality flags will appear here — any entity missing from this period's coverage, any imbalances &gt;1%, any source-PDF parse anomalies. Sourced from the ingestRun document's <code>summary.coverage</code> and related fields.
      </div>

      <PageFooter n={pageNum} total={totalPages} />
    </section>
  );
}
